import { ChangeDetectorRef, ViewChild, OnInit, Component, Input, Output, EventEmitter } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { AuthenticationService, AuthorizationService, StorageManagerService } from '../../../shared/services';
import { Utilities } from '../../../shared/services/utilities';
import { ESGSubmissionService } from '../../../shared/services/esg-submission-service';
import { ISubmission } from '../../../shared/models/submission.model';
import { ISubmissionItem } from '../../../shared/models/submission-item.model';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-esig-modal',
  styleUrls: ['./esig-modal.component.scss'],
  templateUrl: 'esig-modal.component.html'
})
export class EsigModalComponent implements OnInit {
  @ViewChild('esigModal') esigModal: any;
  @ViewChild('postAuthMessageModal') postAuthMessageModal: any;
  @ViewChild('noEsgAccessModal') noEsgAccessModal: any;
  @Output() isSuccessfulySubmitted = new EventEmitter<boolean>(false);
  clientName: string;
  clientId: number;
  clientGuid: string;
  @Input() fileName: string;
  @Input() documentType: string;
  @Input() splVersionNumber: string;
  @Input() formRevisionId: string;
  @Input() orderId: string;
  localFileName: string;
  localDocumentType: string;
  localSplVersionNumber: string;
  localFormRevisionId: string;
  localOrderId: string;
  
  hasEsgAccess: boolean;
  userNameField: any;
  passwordField: any;
  submitterName: string;
  submissionDate: string;
  submitterUserGuid: string;
  submission: ISubmission[] = [];
  submissionItem: ISubmissionItem;
  showloading: boolean = false;

  // Modal for selecting SPL Version
  confirmDialogSelection$: Subject<boolean> = new Subject<boolean>();
  isBuild: boolean;
  invalidSplVersion: boolean;
  updatedSPLVersion: string;
  sendModalVisible: boolean = false;
  sub: Subscription;
  formData: any;

  // Modal properties
  position: object = { X: 'center', Y: 'center' };
  modalWidth = '30%';
  modalHeight = 'auto';
  animationSettings: Object = { effect: 'Zoom' };
  isModal = true;
  subscription: any;
  eSignatureErrorMessage: any;

  constructor(private cdRef: ChangeDetectorRef,
    private storageService: StorageManagerService,
    private authService: AuthenticationService,
    private submissionService: ESGSubmissionService,
    private authorizationService: AuthorizationService,
    private route: ActivatedRoute,
    private toastr: ToastrService) {
  }

  ngOnInit() {
    this.submitterName = typeof this.storageService.read('userData') === 'object' ? this.storageService.read('userData')?.name : '';
    this.submissionDate = new Date().toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const customer = Utilities.getCustomerFromStorage(this.storageService);
    this.clientName = customer ? customer.customerName : '';
    this.clientId = customer ? customer.customerId : null;
    this.clientGuid = customer ? customer.customerGuid : null;
    this.hasEsgAccess = this.authorizationService.hasAccessToAppVariant('SSDrugProducts-ESG', customer);
  }

  setLocalVariables() {
    this.localFileName = this.fileName ? this.fileName.slice() : '';
    this.localDocumentType = this.documentType ? this.documentType.slice() : '';
    this.localFormRevisionId = this.formRevisionId ? this.formRevisionId.slice() : '';
    this.localSplVersionNumber = this.splVersionNumber ? this.splVersionNumber.slice() : '';
    this.localOrderId = this.orderId ? this.orderId.slice() : '';
  }

  showEsignatureModal() {
    if (!this.fileName) {
      return;
    }
    this.setLocalVariables();
    // Clear to initial values
    this.userNameField = document.getElementById('txtUserName');
    this.passwordField = document.getElementById('txtPassword');
    this.userNameField.value = '';
    this.passwordField.value = '';
    this.eSignatureErrorMessage = '';

    if (this.hasEsgAccess) {
      this.sendModalVisible = true;
      this.esigModal.show();
    }
    else {
      this.noEsgAccessModal.show();
    }
  }

  eSigCancel() {
    this.esigModal.hide();
  }

  confirmClicked() {
    const btnConfirm: any = document.getElementById('btnConfirm');
    btnConfirm.disabled = true;
    this.eSignatureErrorMessage = '';

    const paramsContainer = {};

    if (!this.userNameField.value || !this.passwordField.value) {
      btnConfirm.disabled = false;
      this.eSignatureErrorMessage = 'Please enter your email and password'
    }
    else {
      this.showloading = true;
      paramsContainer['userName'] = this.userNameField.value;
      paramsContainer['password'] = this.passwordField.value;
      this.subscription = this.authService.eSigConfirmation(paramsContainer)
        .subscribe(res => {
          btnConfirm.disabled = false;

          if (res?.isSuccess) {
            this.buildDrugProductSubmissionModel();
            this.submissionService.createDrugProductSubmission(this.submission).subscribe(
              async (response) => {
                this.showloading = false;
                this.postAuthMessageModal.show();
                this.esigModal.hide();
                this.isSuccessfulySubmitted.next(true);
                this.submission = [];
              },
              error => {
                this.showloading = false;
                this.toastr.error(`${error && error.errorMessage ? error.errorMessage : error}`);
                this.postAuthMessageModal.hide();
                this.esigModal.hide();
                this.submission = [];
              },
            );
          }
          else {
            this.showloading = false;
            this.eSignatureErrorMessage = res?.message;
            this.submission = [];
          }
        });
    }
  }

  buildDrugProductSubmissionModel() {
    let componentName = this.route.snapshot.component.name;
    let submissionItemType: number;
    let path: string;

    if (componentName.toLowerCase().startsWith('bncc')) {
      submissionItemType = 6; // 6 = SPL-BNCC
      path = `BNCC/Build/${this.localFormRevisionId}`;
    }

    if (componentName.toLowerCase().startsWith('lcr')) {
      submissionItemType = 5; // 5 = SPL-LCR
      path = `LCR/Build/${this.localFormRevisionId}`;
    }

    if (componentName.toLowerCase().startsWith('er')) {
      submissionItemType = 4; // 4 = SPL_ER
      path = `ER/Build/${this.localFormRevisionId}`;
    }

    if (componentName.toLowerCase().startsWith('product')) { // product labeling and product dashboard uses same path and same item type code
      submissionItemType = 3; // 3 = SPL-LL
      // If we are sending a submission from LL, use orderId in file path.
      path = `ORDER/Delivery/${this.localOrderId}`;
    }

    let submissionBuilder = {
      submissionId: 0,
      submissionGuid: '',
      customerId: this.clientId,
      distributionChannel: 'US_FDA_SPL', // hard-coded to this string
      submissionStatusCode: 4, // hard-coded to 4 = SubmissionInProcess
      submissionQueueStatusCode: 2, // hard-coded to 2 = Pending
      submissionTypeCode: 11, // hard-coded to 11 = FdaSubmission
      submissionItems: [{
        submissionId: 0,
        submissionItemTypeCode: submissionItemType,
        rootId: '',
        formRevisionId: this.localFormRevisionId?.toString(),
        sourceFilePath: `${this.clientGuid}/${path}`,
        sourceFileName: this.localFileName.toString(),
      }],
      submissionXMLText: '',
      submissionXMLFileLocation: '',
      submissionCompressedFileCloudLocation: '',
      submissionCompressedFileName: '',
      createUserGuid: this.submitterUserGuid,
      changeUserGuid: this.submitterUserGuid,
      customerGuid: this.clientGuid,
      createDate: '',
      changeDate: '',
      submissionStatusDescription: '',
      submissionCoreId: '',
      submissionItemTypeDescription: '',
      rootId: '',
      formVersion: '',
      formRevisionId: '',
    };

    this.submission.push(submissionBuilder);
  }

  onEmailBlur(event) {
    if (this.userNameField && this.userNameField.value) {
      this.submissionService.getFullNameByEmail(this.userNameField.value)
      .subscribe(res => {
        if (res && res[0]) {
          this.submitterName = res[0].firstName + ' ' + res[0].lastName
          this.submitterUserGuid = res[0].userGUID;
        }
      });
    }
  }

  postAuthClose() {
    this.postAuthMessageModal.hide();
  }

  noAccessModalClose() {
    this.noEsgAccessModal.hide();
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
